import React from 'react';
import {
  Brain, TrendingUp, Target, Zap, Lightbulb, Activity,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const EnergyInnovationAIPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Energy Innovation AI-Driven Analytics',
      title: (
        <>
          Apply pre-trained models <em>to close the generation gap</em>
        </>
      ),
      lede: 'Advanced AI-driven analytics that identify root causes of energy losses and bridge the gap between target and actual generation through innovative energy solutions.',
      image: '/assets/img/general/ai-generated-8685407_1280.jpg',
      imageAlt: 'AI-driven energy analytics',
      actions: [
        { label: 'Start innovation journey', to: '/contact' },
        { label: 'View innovation cases', to: '/case-studies' },
      ],
      metrics: [
        { value: '25%', label: 'Innovation impact' },
        { value: '35%', label: 'Cost reduction' },
        { value: '99.5%', label: 'Accuracy rate' },
        { value: '24/7', label: 'Innovation monitoring' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Innovation capabilities',
      lede: 'Our pre-trained machine learning models are specifically designed to drive energy innovation.',
      checklist: [
        'Drive innovation in energy systems',
        'Increase energy efficiency by 25%',
        'Reduce operational costs by 35%',
        'Enable breakthrough performance improvements',
      ],
      image: '/assets/img/general/steptodown.com254434.jpg',
      imageAlt: 'Renewable energy systems under AI analysis',
      imageCaption: 'Models trained on energy system behaviour, not generic time series',
    }}
    capabilities={{
      eyebrow: 'Key features & benefits',
      title: 'Comprehensive AI-driven analytics',
      lede: 'Designed to drive energy innovation and optimization.',
      items: [
        {
          icon: Brain,
          title: 'AI-powered innovation',
          description: 'Advanced machine learning models that drive energy innovation and optimization.',
        },
        {
          icon: Target,
          title: 'Root cause analysis',
          description: 'Intelligent algorithms identify root causes of energy losses and inefficiencies.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to bridge the gap between target and actual generation.',
        },
        {
          icon: Lightbulb,
          title: 'Innovation insights',
          description: 'Cutting-edge analytics that reveal new opportunities for energy optimization.',
        },
      ],
    }}
    process={{
      eyebrow: 'How our AI drives innovation',
      title: 'From data to deployed improvement',
      lede: 'A comprehensive approach to energy innovation using advanced artificial intelligence.',
      steps: [
        {
          title: 'Data ingestion',
          description: 'Continuous collection of energy system data and performance metrics.',
        },
        {
          title: 'Model analysis',
          description: 'Machine learning models analyze patterns and identify innovation opportunities.',
        },
        {
          title: 'Applied optimization',
          description: 'Real-time implementation of innovative solutions to maximize energy output.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Innovation capabilities',
      title: 'What the models do',
      items: [
        {
          icon: Brain,
          title: 'Machine learning models',
          description: 'Pre-trained models specifically designed for energy innovation and optimization.',
        },
        {
          icon: Zap,
          title: 'Real-time innovation',
          description: 'Instant analysis and optimization recommendations for continuous improvement.',
        },
        {
          icon: Activity,
          title: 'Pattern recognition',
          description: 'Identify complex patterns in energy systems to drive innovation.',
        },
        {
          icon: Lightbulb,
          title: 'Predictive innovation',
          description: 'Anticipate future energy needs and optimize systems accordingly.',
        },
      ],
    }}
    cta={{
      title: 'Ready to drive energy innovation?',
      body: 'Discover how our AI-driven analytics can revolutionize your energy systems.',
      primary: { label: 'Start innovation journey', to: '/contact' },
      secondary: { label: 'View innovation cases', to: '/case-studies' },
    }}
  />
);

export default EnergyInnovationAIPage;
